import {
  POKEMON_TYPE_FIRE,
  POKEMON_TYPE_WATER,
  POKEMON_TYPE_ELECTRIC,
  POKEMON_TYPE_GRASS,
  POKEMON_TYPE_ICE,
  POKEMON_TYPE_FIGHTING,
  POKEMON_TYPE_POISON,
  POKEMON_TYPE_GROUND,
  POKEMON_TYPE_FLYING,
  POKEMON_TYPE_PSYCHIC,
  POKEMON_TYPE_BUG,
  POKEMON_TYPE_ROCK,
  POKEMON_TYPE_GHOST,
  POKEMON_TYPE_DRAGON,
  POKEMON_TYPE_DARK,
  POKEMON_TYPE_STEEL,
  POKEMON_TYPE_FAIRY,
  PokemonType,
} from "../types/type";
import {
  OFFENCE_ITEM_ID_FLAME_PLATE,
  OFFENCE_ITEM_ID_SPLASH_PLATE,
  OFFENCE_ITEM_ID_ZAP_PLATE,
  OFFENCE_ITEM_ID_MEADOW_PLATE,
  OFFENCE_ITEM_ID_ICICLE_PLATE,
  OFFENCE_ITEM_ID_FIST_PLATE,
  OFFENCE_ITEM_ID_TOXIC_PLATE,
  OFFENCE_ITEM_ID_EARTH_PLATE,
  OFFENCE_ITEM_ID_SKY_PLATE,
  OFFENCE_ITEM_ID_MIND_PLATE,
  OFFENCE_ITEM_ID_INSECT_PLATE,
  OFFENCE_ITEM_ID_STONE_PLATE,
  OFFENCE_ITEM_ID_SPOOKY_PLATE,
  OFFENCE_ITEM_ID_DRACO_PLATE,
  OFFENCE_ITEM_ID_DREAD_PLATE,
  OFFENCE_ITEM_ID_IRON_PLATE,
  OFFENCE_ITEM_ID_PIXIE_PLATE,
  POKEMON_ITEM_ID,
} from "../types/item";

type PlateAwarePokemon = {
  id: number;
  species_id?: number;
  type_id_list: number[];
  selected_item_id?: POKEMON_ITEM_ID | null;
};

export const PLATE_TYPE_MAP = {
  [OFFENCE_ITEM_ID_FLAME_PLATE]: POKEMON_TYPE_FIRE,
  [OFFENCE_ITEM_ID_SPLASH_PLATE]: POKEMON_TYPE_WATER,
  [OFFENCE_ITEM_ID_ZAP_PLATE]: POKEMON_TYPE_ELECTRIC,
  [OFFENCE_ITEM_ID_MEADOW_PLATE]: POKEMON_TYPE_GRASS,
  [OFFENCE_ITEM_ID_ICICLE_PLATE]: POKEMON_TYPE_ICE,
  [OFFENCE_ITEM_ID_FIST_PLATE]: POKEMON_TYPE_FIGHTING,
  [OFFENCE_ITEM_ID_TOXIC_PLATE]: POKEMON_TYPE_POISON,
  [OFFENCE_ITEM_ID_EARTH_PLATE]: POKEMON_TYPE_GROUND,
  [OFFENCE_ITEM_ID_SKY_PLATE]: POKEMON_TYPE_FLYING,
  [OFFENCE_ITEM_ID_MIND_PLATE]: POKEMON_TYPE_PSYCHIC,
  [OFFENCE_ITEM_ID_INSECT_PLATE]: POKEMON_TYPE_BUG,
  [OFFENCE_ITEM_ID_STONE_PLATE]: POKEMON_TYPE_ROCK,
  [OFFENCE_ITEM_ID_SPOOKY_PLATE]: POKEMON_TYPE_GHOST,
  [OFFENCE_ITEM_ID_DRACO_PLATE]: POKEMON_TYPE_DRAGON,
  [OFFENCE_ITEM_ID_DREAD_PLATE]: POKEMON_TYPE_DARK,
  [OFFENCE_ITEM_ID_IRON_PLATE]: POKEMON_TYPE_STEEL,
  [OFFENCE_ITEM_ID_PIXIE_PLATE]: POKEMON_TYPE_FAIRY,
} as const satisfies Record<number, PokemonType>;

export const SILVALLY_MEMORY_TYPE_MAP = {
  910: POKEMON_TYPE_FIRE, // ファイヤーメモリ
  911: POKEMON_TYPE_WATER, // ウオーターメモリ
  913: POKEMON_TYPE_ELECTRIC, // エレクトロメモリ
  912: POKEMON_TYPE_GRASS, // グラスメモリ
  915: POKEMON_TYPE_ICE, // アイスメモリ
  902: POKEMON_TYPE_FIGHTING, // ファイトメモリ
  904: POKEMON_TYPE_POISON, // ポイズンメモリ
  905: POKEMON_TYPE_GROUND, // グラウンドメモリ
  903: POKEMON_TYPE_FLYING, // フライングメモリ
  914: POKEMON_TYPE_PSYCHIC, // サイキックメモリ
  907: POKEMON_TYPE_BUG, // バグメモリ
  906: POKEMON_TYPE_ROCK, // ロックメモリ
  908: POKEMON_TYPE_GHOST, // ゴーストメモリ
  916: POKEMON_TYPE_DRAGON, // ドラゴンメモリ
  917: POKEMON_TYPE_DARK, // ダークメモリ
  909: POKEMON_TYPE_STEEL, // スチールメモリ
  918: POKEMON_TYPE_FAIRY, // フェアリーメモリ
} as const satisfies Record<number, PokemonType>;

// タイプ→プレートの逆引きマップを生成する
const TYPE_PLATE_ITEM_MAP: Partial<Record<PokemonType, POKEMON_ITEM_ID>> = Object.entries(PLATE_TYPE_MAP).reduce(
  (acc, [itemId, typeId]) => {
    acc[typeId as PokemonType] = Number(itemId) as POKEMON_ITEM_ID;
    return acc;
  },
  {} as Partial<Record<PokemonType, POKEMON_ITEM_ID>>,
);

/**
 * アイテムIDから対応するプレートタイプ（ポケモンタイプID）を返す。
 * プレートでない場合や未設定時は null。
 */
export function getPlateType(itemId?: POKEMON_ITEM_ID | null): PokemonType | null {
  if (itemId == null) {
    return null;
  }
  return PLATE_TYPE_MAP[itemId] ?? null;
}

/**
 * 渡されたアイテムIDがプレート系かどうかを判定する。
 */
export function isPlateItem(itemId?: POKEMON_ITEM_ID | null): boolean {
  if (itemId == null) {
    return false;
  }
  return itemId in PLATE_TYPE_MAP;
}

/**
 * アイテムIDから対応するメモリタイプ（ポケモンタイプID）を返す。
 * メモリでない場合や未設定時は null。
 */
export function getSilvallyMemoryType(itemId?: POKEMON_ITEM_ID | null): PokemonType | null {
  if (itemId == null) {
    return null;
  }
  return (SILVALLY_MEMORY_TYPE_MAP as Record<number, PokemonType>)[itemId] ?? null;
}

/**
 * タイプIDから対応するプレートのアイテムIDを取得する。
 * プレートが存在しないタイプ（ノーマル/type19など）は null。
 */
export function getPlateItemByType(typeId: PokemonType): POKEMON_ITEM_ID | null {
  return TYPE_PLATE_ITEM_MAP[typeId] ?? null;
}

/**
 * アルセウス（全国図鑑493）向けに、プレート着用時の実効タイプを判定する。
 * それ以外のポケモンは元のタイプ配列をそのまま返す。
 */
export function getArceusEffectiveType(pokemon: PlateAwarePokemon): number[] {
  const speciesId = pokemon.species_id ?? pokemon.id;
  const isArceus = speciesId === 493 || pokemon.id === 493;
  if (!isArceus) {
    const isSilvally = speciesId === 773 || pokemon.id === 773;
    if (!isSilvally) {
      return pokemon.type_id_list;
    }

    const memoryType = getSilvallyMemoryType(pokemon.selected_item_id);
    if (memoryType !== null) {
      return [memoryType];
    }

    return pokemon.type_id_list;
  }

  const plateType = getPlateType(pokemon.selected_item_id);
  if (plateType !== null) {
    return [plateType];
  }

  return pokemon.type_id_list;
}
